/**
 * EditAccessModal — User Entitlement editor.
 *
 * Two layered controls:
 *   1. **Access profile** (Viewer / Operator / Power User / Admin / Owner)
 *      drives the default checkbox grid below.
 *   2. **Feature overrides** — explicit ALLOW / DENY / inherit (default)
 *      per overridable feature.
 *
 * The TENANT PLAN is the commercial ceiling. Features the tenant plan
 * does not include are rendered disabled with a "(plan: …)" hint so the
 * Admin understands why the toggle has no effect.
 *
 * RESERVED features (provider key management, password policy, user
 * management, platform admin tools) are rendered read-only — they are
 * controlled by role-level RBAC, not per-user checkboxes.
 *
 * NEVER exposes provider secrets / passwords / tokens.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import GlassSelect from './GlassSelect';

const API = process.env.REACT_APP_BACKEND_URL;

const PROFILE_OPTIONS = [
  { value: 'viewer',     label: 'Viewer'      },
  { value: 'operator',   label: 'Operator'    },
  { value: 'power_user', label: 'Power User'  },
  { value: 'admin',      label: 'Admin'       },
  { value: 'owner',      label: 'Owner'       },
];

const FEATURE_LABELS = {
  scout:                   'Scout — signal discovery',
  review:                  'Signal review & approval',
  analyse:                 'Analyse — diagnostic scoring',
  fix:                     'Fix — remediation roadmaps',
  fit:                     'Fit — engagement suitability',
  pipeline_dashboard:      'Pipeline dashboard',
  sector_tracker:          'Sector Tracker',
  webhooks:                'Webhooks & alerts',
  saved_investigations:    'Saved investigations',
  export:                  'Bulk export (CSV / PDF)',
  audit_log:               'Audit log access',
  provider_health:         'Provider health (status only)',
  usage_budget_view:       'Usage & budget view',
  api_access:              'Programmatic API access',
  custom_integrations:     'Custom integrations',
  // Reserved — read-only here:
  user_management:         'User management',
  password_policy:         'Password policy',
  provider_key_management: 'Provider key management',
  platform_admin_tools:    'Platform admin tools',
};

const TRI = {
  INHERIT: 'inherit',
  ALLOW:   'allow',
  DENY:    'deny',
};

function triFromOverride(ov) {
  if (ov === true)  return TRI.ALLOW;
  if (ov === false) return TRI.DENY;
  return TRI.INHERIT;
}

function overrideFromTri(tri) {
  if (tri === TRI.ALLOW) return true;
  if (tri === TRI.DENY)  return false;
  return null;
}

export default function EditAccessModal({ user, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [profile, setProfile] = useState('viewer');
  const [originalProfile, setOriginalProfile] = useState('viewer');
  const [overrides, setOverrides] = useState({}); // key -> 'inherit'|'allow'|'deny'
  const [original, setOriginal]   = useState(null); // raw payload for diffing

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await fetch(`${API}/api/users/users/${user.id}/access`);
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`);
        }
        const body = await r.json();
        if (cancelled) return;
        setProfile(body.user_access_profile);
        setOriginalProfile(body.user_access_profile);
        const triMap = {};
        body.features_catalogue.forEach((f) => {
          triMap[f.key] = triFromOverride(f.override);
        });
        setOverrides(triMap);
        setOriginal(body);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load access');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  const panelRef = useRef(null);

  // Modal a11y: Escape closes, Tab cycles within the modal,
  // focus is restored to the previously-focused element on unmount
  // (typically the "Edit access" button that opened us).
  useEffect(() => {
    const previouslyFocused = (typeof document !== 'undefined')
      ? document.activeElement : null;

    const focusables = () => {
      const root = panelRef.current;
      if (!root) return [];
      return Array.from(root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]),' +
        ' select:not([disabled]), textarea:not([disabled]),' +
        ' [tabindex]:not([tabindex="-1"])',
      )).filter((el) => el.offsetParent !== null || el === document.activeElement);
    };

    // Move focus into the modal on mount (first focusable, or the panel).
    const seed = () => {
      const list = focusables();
      if (list.length) list[0].focus();
      else if (panelRef.current) panelRef.current.focus();
    };
    const seedTimer = setTimeout(seed, 0);

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) {
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = list[0];
      const last  = list[list.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !panelRef.current?.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(seedTimer);
      window.removeEventListener('keydown', onKey, true);
      // Restore focus to the trigger where practical.
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try { previouslyFocused.focus(); } catch (_) { /* noop */ }
      }
    };
  }, [onClose]);

  const dirty = useMemo(() => {
    if (!original) return false;
    if (profile !== originalProfile) return true;
    const wanted = {};
    Object.entries(overrides).forEach(([k, v]) => {
      const ov = overrideFromTri(v);
      if (ov !== null && ov !== undefined) wanted[k] = ov;
    });
    const current = original.feature_overrides || {};
    const wantedKeys = Object.keys(wanted).sort().join(',');
    const currentKeys = Object.keys(current).sort().join(',');
    if (wantedKeys !== currentKeys) return true;
    return Object.keys(wanted).some((k) => wanted[k] !== current[k]);
  }, [profile, originalProfile, overrides, original]);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true); setError(null);
    const wanted = {};
    Object.entries(overrides).forEach(([k, v]) => {
      const ov = overrideFromTri(v);
      if (ov !== null && ov !== undefined) wanted[k] = ov;
    });
    try {
      const r = await fetch(`${API}/api/users/users/${user.id}/access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_access_profile: profile,
          feature_overrides: wanted,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        const msg = typeof d.detail === 'string'
          ? d.detail
          : (d.detail?.message || `HTTP ${r.status}`);
        throw new Error(msg);
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to save access');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-access-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 9000, display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', padding: '40px 16px', overflowY: 'auto',
      }}
    >
      <div
        className="qf-glass-panel"
        data-testid="edit-access-modal"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit access for ${user.name}`}
        tabIndex={-1}
        style={{
          width: 'min(720px, 100%)', background: 'rgba(255,255,255,0.97)',
          borderRadius: 12, padding: 20, boxShadow: '0 24px 60px rgba(15,23,42,0.28)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
              Edit access — {user.name}
            </h3>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {user.email} · Role: <strong>{user.role_label}</strong>
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={onClose}
                  data-testid="edit-access-close" disabled={saving}>Close</button>
        </div>

        <div style={{
          fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14,
          padding: 10, background: 'rgba(186,151,123,0.07)',
          border: '1px solid rgba(186,151,123,0.25)', borderRadius: 8,
        }} data-testid="edit-access-explainer">
          <strong>Tenant plan controls availability.</strong> User access controls what
          this person can use inside the tenant allowance. A feature greyed out below
          is not part of the tenant plan and cannot be granted to a single user.
        </div>

        {loading && (
          <div data-testid="edit-access-loading" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
            Loading access…
          </div>
        )}

        {!loading && original && (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
                Access profile
              </label>
              <div style={{ marginTop: 4 }}>
                <GlassSelect
                  value={profile}
                  onChange={(e) => setProfile(e.target.value)}
                  data-testid="edit-access-profile"
                  disabled={saving}
                  style={{ width: '100%', padding: 6, border: '1px solid var(--border)', borderRadius: 6 }}
                >
                  {PROFILE_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </GlassSelect>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                Profile sets the default feature checkboxes below. Use ALLOW / DENY
                to override the profile for an individual feature.
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: 6 }}>
                Feature access
              </div>

              <div data-testid="edit-access-feature-grid"
                   style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                {original.features_catalogue.map((f) => {
                  const tri = overrides[f.key] || TRI.INHERIT;
                  const planAllowed = !!f.plan_allowed;
                  const reserved = !!f.reserved;
                  const disabled = !planAllowed || reserved || saving;
                  const effective = planAllowed && !reserved
                    ? (tri === TRI.ALLOW ? true : (tri === TRI.DENY ? false : f.profile_default))
                    : f.effective;
                  return (
                    <div
                      key={f.key}
                      data-testid={`feature-row-${f.key}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 8,
                        alignItems: 'center',
                        padding: '6px 10px',
                        borderRadius: 6,
                        background: reserved ? 'rgba(148,163,184,0.08)' : 'transparent',
                        opacity: !planAllowed ? 0.55 : 1,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>
                          {FEATURE_LABELS[f.key] || f.key}
                          {reserved && (
                            <span style={{ fontSize: 10, marginLeft: 8, padding: '2px 6px', borderRadius: 999, background: 'rgba(148,163,184,0.2)', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Role-controlled
                            </span>
                          )}
                          {!planAllowed && (
                            <span style={{ fontSize: 10, marginLeft: 8, padding: '2px 6px', borderRadius: 999, background: 'rgba(245,158,11,0.18)', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Not in plan
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                          Profile default: <strong>{f.profile_default ? 'Allow' : 'Deny'}</strong>
                          {' · '}Effective: <strong>{effective ? 'Allow' : 'Deny'}</strong>
                        </div>
                      </div>
                      <GlassSelect
                        value={tri}
                        onChange={(e) => setOverrides({ ...overrides, [f.key]: e.target.value })}
                        data-testid={`feature-override-${f.key}`}
                        disabled={disabled}
                        style={{ padding: 4, fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, minWidth: 110 }}
                      >
                        <option value={TRI.INHERIT}>Inherit</option>
                        <option value={TRI.ALLOW}>Allow</option>
                        <option value={TRI.DENY}>Deny</option>
                      </GlassSelect>
                    </div>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="auth-error" data-testid="edit-access-error" style={{ marginTop: 10 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm"
                      onClick={onClose}
                      disabled={saving}
                      data-testid="edit-access-cancel">Cancel</button>
              <button className="btn btn-primary btn-sm"
                      onClick={save}
                      disabled={!dirty || saving}
                      data-testid="edit-access-save">
                {saving ? 'Saving…' : 'Save access'}
              </button>
            </div>
          </>
        )}

        {!loading && error && !original && (
          <div className="auth-error" data-testid="edit-access-error" style={{ marginTop: 10 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
